import React from 'react';
import { Loader } from '@saviynt/design-system';
import classNames from 'classnames';

import { type MenuId, RequestStatus } from '../../../models';
import { Link, P, useLocalize } from '../../../utilities';

import { kpiLabels } from './messages';

import './KpiTile.css';

interface Props {
  id: MenuId;
  link?: string;
  status?: RequestStatus;
  metric?: number | 'error';
  className?: string;
}

/**
 * View component for a single KPI navigation tile.
 */
export default function KpiTile({
  id,
  link = '',
  status,
  metric,
  className,
}: Props): JSX.Element | null {
  const localize = useLocalize();
  const localizeId = `Nav.KpisWidget.Kpi-${id}`;
  const label = localize(
    {
      id: localizeId,
      defaultMessage: kpiLabels[localizeId],
    },
    { count: metric, hide: () => '' }
  );

  if (metric === 'error') {
    // See commit 08624295f6 for implementation with error message tile.
    return null;
  }

  if (status !== RequestStatus.Idle || metric === undefined) {
    const loadingClassName = classNames(
      'Nav_KpiTile',
      'Nav_KpiTile--loading',
      className
    );

    return (
      <Link href={link} className={loadingClassName}>
        <Loader kind='dots' format='inline' color='brand' size='small' />
      </Link>
    );
  }

  const metricClassName = classNames('Nav_KpiTile', className);

  return (
    <Link href={link} className={metricClassName}>
      <div className='Nav_KpiTile-main'>
        <div className='Nav_KpiTile-metric'>{metric}</div>
        <P className='Nav_KpiTile-label'>{label}</P>
      </div>
    </Link>
  );
}
